/**
 * Hook: Push-Onboarding-State
 *
 * Entscheidet, ob dem User aktuell der Onboarding-Banner angezeigt werden soll.
 *
 * Sichtbar wenn ALLE folgenden Bedingungen zutreffen:
 *   1. Browser unterstützt Push überhaupt
 *   2. Permission ist 'default' (User wurde noch nicht gefragt)
 *   3. Es gibt noch keine aktive Subscription auf diesem Gerät
 *   4. User hat den Banner nicht innerhalb der letzten 14 Tage weggeklickt
 *      (Snooze via localStorage)
 *
 * iOS-Spezial-Hinweis:
 *   Wenn iOS aber NICHT im Standalone-Mode → andere Message anzeigen
 *   ("Bitte zum Home-Bildschirm hinzufügen") statt Push direkt.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  enablePushNotifications,
  getPushCapability,
  hasActivePushSubscription,
  type PushCapability
} from '../services/support/pushSubscriptionService';
import { updateNotificationPreferences } from '../services/support/notificationPreferencesService';

const SNOOZE_KEY = 'rf-push-snooze-until';
const SNOOZE_DURATION_MS = 14 * 24 * 60 * 60 * 1000; // 14 Tage

interface UsePushOnboardingResult {
  /** Soll der Banner gezeigt werden? */
  shouldShowBanner: boolean;
  /** Aktueller Capability-State (Browser/iOS/Permission) */
  capability: PushCapability;
  /** Aktiviert Push (zeigt System-Dialog) */
  enable: () => Promise<void>;
  /** Banner für 14 Tage ausblenden */
  snooze: () => void;
  /** Letzter Fehler beim Aktivieren */
  error: string | null;
  /** Während des Aktivierungs-Vorgangs true */
  isWorking: boolean;
}

function isSnoozed(): boolean {
  if (typeof window === 'undefined') return false;
  const stored = localStorage.getItem(SNOOZE_KEY);
  if (!stored) return false;
  const until = parseInt(stored, 10);
  if (isNaN(until)) return false;
  return Date.now() < until;
}

interface UsePushOnboardingOptions {
  enabled?: boolean;
  /**
   * Wenn true: 14-Tage-Snooze ignorieren. Sinnvoll für Admin-Inbox, wo der
   * Banner kein Werbe-Element ist sondern Setup-Anleitung.
   */
  ignoreSnooze?: boolean;
}

export function usePushOnboarding(options: UsePushOnboardingOptions = {}): UsePushOnboardingResult {
  const enabledFlag = options.enabled !== false;
  const [capability, setCapability] = useState<PushCapability>(() => getPushCapability());
  const [hasSubscription, setHasSubscription] = useState<boolean>(false);
  const [snoozed, setSnoozed] = useState<boolean>(() => isSnoozed());
  const [error, setError] = useState<string | null>(null);
  const [isWorking, setIsWorking] = useState<boolean>(false);

  // Capability + Subscription beim Mount + bei sichtbarer Tab-Änderung re-prüfen
  useEffect(() => {
    if (!enabledFlag) return;
    let cancelled = false;
    const refresh = async (): Promise<void> => {
      const cap = getPushCapability();
      if (cancelled) return;
      setCapability(cap);
      if (cap.isSupported) {
        try {
          const has = await hasActivePushSubscription();
          if (!cancelled) setHasSubscription(has);
        } catch {
          /* Egal — Banner würde im Zweifel erscheinen */
        }
      }
    };
    void refresh();

    const onVis = (): void => {
      if (document.visibilityState === 'visible') void refresh();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [enabledFlag]);

  const enable = useCallback(async (): Promise<void> => {
    setError(null);
    setIsWorking(true);
    try {
      await enablePushNotifications();
      setHasSubscription(true);
      setCapability(getPushCapability());
      // Profile-Toggle synchron halten — sonst zeigt der User-Toggle "aus"
      // obwohl tatsächlich eine aktive Subscription besteht.
      // Best-Effort: Fehler hier nicht blockierend (Sub ist trotzdem da).
      void updateNotificationPreferences({ webPushEnabled: true }).catch((err) => {
        console.warn('[PushOnboarding] webPushEnabled-Sync fehlgeschlagen', err);
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Aktivierung fehlgeschlagen.');
    } finally {
      setIsWorking(false);
    }
  }, []);

  const snooze = useCallback((): void => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_DURATION_MS));
    }
    setSnoozed(true);
  }, []);

  // Banner-Sichtbarkeit
  // Standard-Onboarding: Browser unterstützt Push, Permission noch nicht gefragt, keine Sub
  // iOS-PWA-Hinweis: iOS aber nicht im Standalone-Mode (Push wäre nach PWA-Install möglich,
  //   daher Hinweis zeigen, auch wenn isSupported aktuell false ist).
  const isIOSWithoutPWA = capability.isIOS && !capability.isStandalone;
  const standardEligible =
    capability.isSupported && capability.permission === 'default' && !hasSubscription;
  const effectivelySnoozed = snoozed && !options.ignoreSnooze;
  const shouldShowBanner =
    enabledFlag && !effectivelySnoozed && (standardEligible || isIOSWithoutPWA);

  return { shouldShowBanner, capability, enable, snooze, error, isWorking };
}
